import axios from 'axios';
import { useEffect, useState } from 'react';
import { BiCheck, BiEdit, BiTrash } from 'react-icons/bi';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import ReactPaginate from 'react-paginate';
import SearchBar from '../SearchBar';
import AddExpertForm from './AddExpertForm';
import EditExpertForm from './EditExpertForm';
import './Expert.css';

// const BASE_URL = "http://localhost:8000";
const BASE_URL = "https://idealpursuits.co.in/api";

function Expert() {
  const [experts, setExperts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const fetchExperts = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/expert/getExpertsDetailsForAdmin.php`);
      setExperts(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching experts:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const filteredExperts = experts.filter((expert) =>
    Object.values(expert).some(
      (value) =>
        typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const pageCount = Math.ceil(filteredExperts.length / itemsPerPage);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleAddExpert = () => {
    setShowAddModal(true);
  };

  const handleEditExpert = (expert) => {
    setSelectedExpert(expert);
    setShowEditModal(true);
  };

  const handleAddSubmit = async (formData) => {
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await axios.post(`${BASE_URL}/expert/addExpertDetails.php`, formData);

      const result = response.data;
      if (result.success) {
        alert('Expert Added Successfully!');
        setShowAddModal(false);
        fetchExperts();
      } else {
        alert(result.error || 'Failed to add expert. Please try again.');
      }
    } catch (error) {
      console.error('Error adding expert:', error);
      if (error.response && error.response.data.error) {
        alert(`Error: ${error.response.data.error}`);
      } else {
        alert('Failed to add expert. Please try again.');
      }
    }
  };

  const handleEditSubmit = async (formData) => {
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      const response = await axios.post(`${BASE_URL}/expert/updateExpertDetails.php`, formData);
      
      const result = response.data;
      if (result.success) {
        alert('Expert Updated Successfully!');
        setShowEditModal(false);
        fetchExperts();
      } else {
        alert('Failed to update expert. Please try again.');
      }
    } catch (error) {
      console.error('Error updating expert:', error);
      alert('Failed to update expert. Please try again.');
    }
  };

  const handleApproveExpert = async (expertId, email, name) => {
    try {
      const response = await axios.put(`${BASE_URL}/expert/approveExpertDetails.php`,{ isApproved: true, email, name, expertId });

      const result = await response.data;
      if (result.success) {
        alert('Expert Approved Successfully!');
        fetchExperts();
      } else {
        alert('Failed to approve expert. Please try again.');
      }
    } catch (error) {
      console.error('Error approving expert:', error);
      alert('Failed to approve expert. Please try again.');
    }
  };

  const handleDeleteExpert = async (expertId) => {
    try {
      const confirmation = window.confirm("Are you sure you want to delete this expert? This action cannot be undone.");
  
      if (!confirmation) {
        return; 
      }
  
      const response = await axios.put(`${BASE_URL}/expert/deleteExpert.php`, { expertId });
  
      const result = await response.data;
      if (result.success) {
        alert('Expert Deleted Successfully!');
        fetchExperts();
      } else {
        alert('Failed to delete expert. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting expert:', error);
      alert('Failed to delete expert. Please try again.');
    }
  };  

  const formatDateToDisplay = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div>
      <h2>Experts</h2>
      <div className="search-bar">
        <SearchBar onSearch={handleSearch} />
      </div>
      {isLoading ? (
        <p>Loading experts...</p>
      ) : (
        <div>
          <button className='add-button' onClick={handleAddExpert}>Add Expert</button>

          {showAddModal && (
            <AddExpertForm
              onSubmit={handleAddSubmit}
              onClose={() => setShowAddModal(false)}
            />
          )}

          {showEditModal && (
            <EditExpertForm
              expert={selectedExpert}
              onSubmit={handleEditSubmit}
              onClose={() => setShowEditModal(false)}
            />
          )}

          <table>
            <thead>
              <tr>
                <th>Profile Image</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Qualification</th>
                <th>Approval Status</th>
                <th>Registration Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExperts
                .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
                .map((expert) => (
                  <tr key={expert.expertId}>
                    <td>
                      {expert.profileImageName ? (
                        <img
                          src={`${BASE_URL}/images/profile/${expert.profileImageName}`}
                          alt={expert.name}
                          className='profile-image'
                        />
                      ) : (
                        <p>No Image</p>
                      )}
                    </td>
                    <td>{expert.name}</td>
                    <td>{expert.email}</td>
                    <td>{expert.phone}</td>
                    <td>{expert.qualifications}</td>
                    <td>{expert.isApproved ? 'Approved' : 'Pending'}</td>
                    <td>{formatDateToDisplay(expert.createdAt)}</td>
                    <td>
                      <button onClick={() => handleEditExpert(expert)}>
                        <BiEdit />
                      </button>
                      {!expert.isApproved && (
                        <button
                          className='approve-button'
                          onClick={() => handleApproveExpert(expert.expertId, expert.email, expert.name)}
                        >
                          <BiCheck />
                        </button>
                      )}
                      <button onClick={() => handleDeleteExpert(expert.expertId)}>
                        <BiTrash />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <ReactPaginate
            previousLabel={<FaChevronLeft />}
            nextLabel={<FaChevronRight />}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageChange}
            containerClassName={'pagination'}
            activeClassName={'active'}
            forcePage={currentPage}
          />
        </div>
      )}
    </div>
  );
}

export default Expert;
